/**
 * AdminProfessionalsManagement - Blueprint CMS Panel
 * ===================================================
 * Full CRUD for managing professionals (Retailers, Fabricators, Advisors)
 * All data stored in Supabase - NO hardcoded placeholders
 * 
 * @version 1.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Plus, Edit, Trash2, Loader2, Check, X, Eye, 
  Building2, MapPin, Star, Phone, Mail, ExternalLink,
  ChevronDown, ChevronRight, MoreVertical, RefreshCw,
  Upload, Filter, Download, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import supabase from '../../lib/supabase';

// Professional type options
const PROFESSIONAL_TYPES = [
  { value: 'retailer', label: 'Rivenditore' },
  { value: 'fabricator', label: 'Fabbricante' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'showroom', label: 'Showroom' }
];

// Approval status options
const APPROVAL_STATUS = [
  { value: 'pending', label: 'In attesa', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'approved', label: 'Approvato', color: 'bg-green-500/20 text-green-400' },
  { value: 'rejected', label: 'Rifiutato', color: 'bg-red-500/20 text-red-400' },
  { value: 'suspended', label: 'Sospeso', color: 'bg-white/10 text-white/50' }
];

// Service options
const SERVICES_OPTIONS = [
  { value: 'consulenza_materiale', label: 'Consulenza materiale' },
  { value: 'campionatura', label: 'Campionatura' },
  { value: 'taglio_misura', label: 'Taglio su misura' },
  { value: 'lavorazione_cnc', label: 'Lavorazione CNC' },
  { value: 'installazione', label: 'Installazione' },
  { value: 'logistica_consegna', label: 'Logistica e consegna' },
  { value: 'supporto_progettuale', label: 'Supporto progettuale' }
];

// Specialization options
const SPECIALIZATIONS_OPTIONS = [
  { value: 'marmi_italiani', label: 'Marmi italiani' },
  { value: 'marmi_esteri', label: 'Marmi esteri' },
  { value: 'quarziti', label: 'Quarziti' },
  { value: 'graniti', label: 'Graniti' },
  { value: 'onici', label: 'Onici' },
  { value: 'travertini', label: 'Travertini' },
  { value: 'pietre_sinterizzate', label: 'Pietre sinterizzate' },
  { value: 'pietra_naturale', label: 'Pietra naturale' }
];

/**
 * Professional Row Component
 */
const ProfessionalRow = ({ professional, onEdit, onDelete, onToggleStatus, onViewMaterials }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showActions, setShowActions] = useState(false);
  
  const statusInfo = APPROVAL_STATUS.find(s => s.value === professional.approval_status) || APPROVAL_STATUS[0];
  
  return (
    <>
      <tr className="border-b border-[#1a1a1a] hover:bg-[#0f0f0f]">
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1a1a1a] overflow-hidden flex-shrink-0">
              {professional.logo_url ? (
                <img src={professional.logo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white/20" />
                </div>
              )}
            </div>
            <div>
              <p className="text-white font-medium">{professional.business_name}</p>
              <p className="text-white/40 text-xs">{professional.city}, {professional.province}</p>
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          <span className="px-2 py-1 rounded text-xs bg-[#1a1a1a] text-white/60 capitalize">
            {PROFESSIONAL_TYPES.find(t => t.value === professional.professional_type)?.label || professional.professional_type}
          </span>
        </td>
        <td className="py-4 px-4">
          <span className={`px-2 py-1 rounded text-xs ${statusInfo.color}`}>
            {statusInfo.label}
          </span>
        </td>
        <td className="py-4 px-4">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-[#B08B5C] fill-[#B08B5C]" />
            <span className="text-white/60 text-sm">{professional.rating?.toFixed(1) || '-'}</span>
          </div>
        </td>
        <td className="py-4 px-4">
          <span className="text-white/40 text-sm">{professional.total_leads || 0}</span>
        </td>
        <td className="py-4 px-4 text-right">
          <div className="relative">
            <button 
              onClick={() => setShowActions(!showActions)}
              className="p-2 rounded hover:bg-[#1a1a1a]"
            >
              <MoreVertical className="w-4 h-4 text-white/40" />
            </button>
            
            {showActions && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowActions(false)} 
                />
                <div className="absolute right-0 top-full mt-1 bg-[#121212] border border-[#1a1a1a] rounded-lg shadow-xl z-20 w-48">
                  <button 
                    onClick={() => { setIsExpanded(!isExpanded); setShowActions(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-white/70 hover:bg-[#1a1a1a] flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" /> Vedi dettagli
                  </button>
                  <button 
                    onClick={() => { onEdit(professional); setShowActions(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-white/70 hover:bg-[#1a1a1a] flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" /> Modifica
                  </button>
                  <button 
                    onClick={() => { onViewMaterials(professional); setShowActions(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-white/70 hover:bg-[#1a1a1a] flex items-center gap-2"
                  >
                    <Building2 className="w-4 h-4" /> Materiali
                  </button>
                  <hr className="border-[#1a1a1a] my-1" />
                  {professional.approval_status === 'approved' ? (
                    <button 
                      onClick={() => { onToggleStatus(professional, 'suspended'); setShowActions(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-yellow-400 hover:bg-[#1a1a1a] flex items-center gap-2"
                    >
                      <AlertCircle className="w-4 h-4" /> Sospendi
                    </button>
                  ) : (
                    <button 
                      onClick={() => { onToggleStatus(professional, 'approved'); setShowActions(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-green-400 hover:bg-[#1a1a1a] flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" /> Approva
                    </button>
                  )}
                  <button 
                    onClick={() => { onDelete(professional); setShowActions(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-[#1a1a1a] flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Elimina
                  </button>
                </div>
              </>
            )}
          </div>
        </td>
      </tr>
      
      {/* Expanded Details */}
      {isExpanded && (
        <tr className="bg-[#0f0f0f]">
          <td colSpan={6} className="p-4">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <h4 className="text-white/40 text-xs mb-2 uppercase">Contatto</h4>
                <div className="space-y-1 text-sm">
                  {professional.phone && (
                    <p className="text-white/70 flex items-center gap-2">
                      <Phone className="w-3 h-3" /> {professional.phone}
                    </p>
                  )}
                  {professional.email && (
                    <p className="text-white/70 flex items-center gap-2">
                      <Mail className="w-3 h-3" /> {professional.email}
                    </p>
                  )}
                  {professional.website && (
                    <a href={professional.website} target="_blank" rel="noreferrer" className="text-[#B08B5C] flex items-center gap-2">
                      <ExternalLink className="w-3 h-3" /> Sito web
                    </a>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-white/40 text-xs mb-2 uppercase">Indirizzo</h4>
                <p className="text-white/70 text-sm">
                  {professional.address}<br />
                  {professional.postal_code} {professional.city} ({professional.province})
                </p>
              </div>
              <div>
                <h4 className="text-white/40 text-xs mb-2 uppercase">Specializzazioni</h4>
                <div className="flex flex-wrap gap-1">
                  {(professional.specializations || []).map((spec, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded bg-[#1a1a1a] text-white/60">
                      {spec.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * Professional Form Modal
 */
const ProfessionalFormModal = ({ professional, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    professional_type: 'retailer',
    description: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    country: 'Italia',
    latitude: '',
    longitude: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    logo_url: '',
    cover_image_url: '',
    opening_hours: {},
    specializations: [],
    services: [],
    is_verified: false,
    is_active: true,
    approval_status: 'pending',
    ...professional
  });
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.city) {
      toast.error('Nome azienda e città sono obbligatori');
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const toggleArrayItem = (field, item) => {
    const current = formData[field] || [];
    const updated = current.includes(item)
      ? current.filter(i => i !== item)
      : [...current, item];
    updateField(field, updated);
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#121212] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col border border-[#1a1a1a]">
        {/* Header */}
        <div className="p-6 border-b border-[#1a1a1a] flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-white">
            {professional?.id ? 'Modifica professionista' : 'Nuovo professionista'}
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="text-white/40 text-xs uppercase tracking-wider">Informazioni base</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Nome azienda *</label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => updateField('business_name', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Tipologia *</label>
                <select
                  value={formData.professional_type}
                  onChange={(e) => updateField('professional_type', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                >
                  {PROFESSIONAL_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-white/60 block mb-1.5">Descrizione</label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none resize-none"
              />
            </div>
          </div>
          
          {/* Location */}
          <div className="space-y-4">
            <h4 className="text-white/40 text-xs uppercase tracking-wider">Posizione</h4>
            
            <div>
              <label className="text-sm text-white/60 block mb-1.5">Indirizzo</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
              />
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2">
                <label className="text-sm text-white/60 block mb-1.5">Città *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Provincia</label>
                <input
                  type="text"
                  value={formData.province}
                  onChange={(e) => updateField('province', e.target.value)}
                  maxLength={2}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none uppercase"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1.5">CAP</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => updateField('postal_code', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Latitudine</label>
                <input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => updateField('latitude', parseFloat(e.target.value) || '')}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Longitudine</label>
                <input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => updateField('longitude', parseFloat(e.target.value) || '')}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
            </div>
          </div>
          
          {/* Contact */}
          <div className="space-y-4">
            <h4 className="text-white/40 text-xs uppercase tracking-wider">Contatti</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Telefono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1.5">WhatsApp</label>
                <input
                  type="tel"
                  value={formData.whatsapp}
                  onChange={(e) => updateField('whatsapp', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Sito web</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
            </div>
          </div>
          
          {/* Images */}
          <div className="space-y-4">
            <h4 className="text-white/40 text-xs uppercase tracking-wider">Immagini</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/60 block mb-1.5">URL Logo</label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => updateField('logo_url', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1.5">URL Copertina</label>
                <input
                  type="url"
                  value={formData.cover_image_url}
                  onChange={(e) => updateField('cover_image_url', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                />
              </div>
            </div>
          </div>
          
          {/* Specializations */}
          <div className="space-y-4">
            <h4 className="text-white/40 text-xs uppercase tracking-wider">Specializzazioni</h4>
            <div className="flex flex-wrap gap-2">
              {SPECIALIZATIONS_OPTIONS.map(spec => (
                <button
                  key={spec.value}
                  type="button"
                  onClick={() => toggleArrayItem('specializations', spec.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    (formData.specializations || []).includes(spec.value)
                      ? 'bg-[#B08B5C] text-black'
                      : 'bg-[#1a1a1a] text-white/60 hover:text-white'
                  }`}
                >
                  {spec.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Services */}
          <div className="space-y-4">
            <h4 className="text-white/40 text-xs uppercase tracking-wider">Servizi offerti</h4>
            <div className="flex flex-wrap gap-2">
              {SERVICES_OPTIONS.map(service => (
                <button
                  key={service.value}
                  type="button"
                  onClick={() => toggleArrayItem('services', service.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    (formData.services || []).includes(service.value)
                      ? 'bg-[#B08B5C] text-black'
                      : 'bg-[#1a1a1a] text-white/60 hover:text-white'
                  }`}
                >
                  {service.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Status */}
          <div className="space-y-4">
            <h4 className="text-white/40 text-xs uppercase tracking-wider">Stato</h4>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-white/60 block mb-1.5">Stato approvazione</label>
                <select
                  value={formData.approval_status}
                  onChange={(e) => updateField('approval_status', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
                >
                  {APPROVAL_STATUS.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
              
              <label className="flex items-center gap-3 cursor-pointer pt-6">
                <input
                  type="checkbox"
                  checked={formData.is_verified}
                  onChange={(e) => updateField('is_verified', e.target.checked)}
                  className="w-4 h-4 rounded border-[#333] bg-[#0a0a0a] text-[#B08B5C] focus:ring-[#B08B5C]"
                />
                <span className="text-white/70 text-sm">Verificato</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer pt-6">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => updateField('is_active', e.target.checked)}
                  className="w-4 h-4 rounded border-[#333] bg-[#0a0a0a] text-[#B08B5C] focus:ring-[#B08B5C]"
                />
                <span className="text-white/70 text-sm">Attivo</span>
              </label>
            </div>
          </div>
        </form>
        
        {/* Footer */}
        <div className="p-6 border-t border-[#1a1a1a] flex gap-3 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1 border-[#1a1a1a] text-white/60"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex-1 bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {professional?.id ? 'Salva modifiche' : 'Crea professionista'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Main AdminProfessionalsManagement Component
 */
const AdminProfessionalsManagement = () => {
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    totalLeads: 0
  });
  
  // Load professionals
  const loadProfessionals = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('professionals')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setProfessionals(data || []);
      
      // Calculate stats
      setStats({
        total: data?.length || 0,
        approved: data?.filter(p => p.approval_status === 'approved').length || 0,
        pending: data?.filter(p => p.approval_status === 'pending').length || 0,
        totalLeads: data?.reduce((sum, p) => sum + (p.total_leads || 0), 0) || 0
      });
    } catch (err) {
      console.error('Error loading professionals:', err);
      toast.error('Errore nel caricamento');
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);
  
  // Save professional
  const handleSave = async (data) => {
    try {
      if (data.id) {
        // Update
        const { error } = await supabase
          .from('professionals')
          .update({
            ...data,
            updated_at: new Date().toISOString()
          })
          .eq('id', data.id);
        
        if (error) throw error;
        toast.success('Professionista aggiornato!');
      } else {
        // Insert
        const { error } = await supabase
          .from('professionals')
          .insert({
            ...data,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        
        if (error) throw error;
        toast.success('Professionista creato!');
      }
      
      loadProfessionals();
    } catch (err) {
      console.error('Save error:', err);
      throw err;
    }
  };
  
  // Delete professional
  const handleDelete = async (professional) => {
    if (!confirm(`Eliminare "${professional.business_name}"? Questa azione è irreversibile.`)) return;
    
    try {
      const { error } = await supabase
        .from('professionals')
        .delete()
        .eq('id', professional.id);
      
      if (error) throw error;
      toast.success('Professionista eliminato');
      loadProfessionals();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Errore nell\'eliminazione');
    }
  };
  
  // Toggle status
  const handleToggleStatus = async (professional, newStatus) => {
    try {
      const { error } = await supabase
        .from('professionals')
        .update({ 
          approval_status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', professional.id);
      
      if (error) throw error;
      toast.success(`Stato aggiornato a: ${APPROVAL_STATUS.find(s => s.value === newStatus)?.label}`);
      loadProfessionals();
    } catch (err) {
      console.error('Status update error:', err);
      toast.error('Errore nell\'aggiornamento');
    }
  };
  
  // Filtered professionals
  const filteredProfessionals = professionals.filter(p => {
    if (filterType !== 'all' && p.professional_type !== filterType) return false;
    if (filterStatus !== 'all' && p.approval_status !== filterStatus) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        p.business_name?.toLowerCase().includes(term) ||
        p.city?.toLowerCase().includes(term) ||
        p.email?.toLowerCase().includes(term)
      );
    }
    return true;
  });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Gestione Professionisti</h2>
          <p className="text-white/40 text-sm">Retailer, Fabricators, Advisors nel network</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={loadProfessionals}
            className="border-[#1a1a1a] text-white/60"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
          <Button
            onClick={() => { setEditTarget(null); setShowForm(true); }}
            className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo professionista
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
          <p className="text-2xl font-bold text-white">{stats.total}</p>
          <p className="text-white/40 text-xs">Totale</p>
        </div>
        <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
          <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
          <p className="text-white/40 text-xs">Approvati</p>
        </div>
        <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
          <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
          <p className="text-white/40 text-xs">In attesa</p>
        </div>
        <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
          <p className="text-2xl font-bold text-[#B08B5C]">{stats.totalLeads}</p>
          <p className="text-white/40 text-xs">Leads totali</p>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cerca per nome, città, email..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
        >
          <option value="all">Tutte le tipologie</option>
          {PROFESSIONAL_TYPES.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white text-sm focus:border-[#B08B5C] focus:outline-none"
        >
          <option value="all">Tutti gli stati</option>
          {APPROVAL_STATUS.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
      </div>
      
      {/* Table */}
      <div className="bg-[#0a0a0a] rounded-xl border border-[#1a1a1a] overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
          </div>
        ) : filteredProfessionals.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 mx-auto text-white/10 mb-4" />
            <p className="text-white/50">Nessun professionista trovato</p>
            <Button
              onClick={() => { setEditTarget(null); setShowForm(true); }}
              className="mt-4 bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi il primo
            </Button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1a1a1a] text-left">
                <th className="py-3 px-4 text-white/40 text-xs font-medium uppercase">Azienda</th>
                <th className="py-3 px-4 text-white/40 text-xs font-medium uppercase">Tipologia</th>
                <th className="py-3 px-4 text-white/40 text-xs font-medium uppercase">Stato</th>
                <th className="py-3 px-4 text-white/40 text-xs font-medium uppercase">Rating</th>
                <th className="py-3 px-4 text-white/40 text-xs font-medium uppercase">Leads</th>
                <th className="py-3 px-4 text-white/40 text-xs font-medium uppercase text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredProfessionals.map(pro => (
                <ProfessionalRow
                  key={pro.id}
                  professional={pro}
                  onEdit={(p) => { setEditTarget(p); setShowForm(true); }}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                  onViewMaterials={(p) => toast.info(`Materiali di ${p.business_name}`)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Form Modal */}
      {showForm && (
        <ProfessionalFormModal
          professional={editTarget}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default AdminProfessionalsManagement;
